import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, Save, Copy } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MappingSection } from './MappingSection';
import { KeyTotals } from './KeyTotals';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTIONS = [
  { key: 'shop', label: 'Departments & Services' },
  { key: 'cards', label: 'Cards & Payments' },
  { key: 'paid_outs', label: 'Paid Outs' },
  { key: 'bunkering', label: 'Bunkering Cards' },
];
const errorText = (e, fallback) => e.response?.data?.detail || fallback;

export default function TillMappingPage() {
  const { stores } = useStore();
  const tillStores = (stores || []).filter(s => ['fuel_station', 'convenience_store'].includes(s.store_type));
  const [storeId, setStoreId] = useState('');
  const [data, setData] = useState(null);
  const [mappings, setMappings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);
  const fileRef = useRef(null);

  const load = (payload) => { setData(payload); setMappings(payload.mappings); setDirty(false); };

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const changeStore = (id) => {
    if (dirty && !window.confirm('You have unsaved mapping changes. Discard them?')) return;
    setCopyFrom('');
    setStoreId(id);
  };

  useEffect(() => {
    if (!storeId) return;
    setData(null);
    axios.get(`${API}/till-mapping/${storeId}`).then(r => load(r.data))
      .catch(e => toast.error(errorText(e, 'Failed to load till mapping')));
  }, [storeId]);

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } finally { setBusy(''); }
  };

  const upload = (file) => run('upload', async () => {
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/till-mapping/${storeId}/upload`, form);
      load(r.data);
      toast.success('Report read - names updated');
    } catch (e) { toast.error(errorText(e, 'Could not read the report')); }
    if (fileRef.current) fileRef.current.value = '';
  });

  const save = () => run('save', async () => {
    try {
      load((await axios.put(`${API}/till-mapping/${storeId}`, { mappings })).data);
      toast.success('Till mapping saved - used on every new Day End upload');
    } catch (e) { toast.error(errorText(e, 'Failed to save')); }
  });

  const copy = () => run('copy', async () => {
    try {
      const r = await axios.post(`${API}/till-mapping/${storeId}/copy-from/${copyFrom}`);
      load(r.data);
      toast.success(`Copied ${r.data.copied} links`);
    } catch (e) { toast.error(errorText(e, 'Failed to copy')); }
  });

  const setLink = (category, key, value) => {
    setMappings(m => {
      const next = { ...(m[category] || {}) };
      if (value) next[key] = value; else delete next[key];
      return { ...m, [category]: next };
    });
    setDirty(true);
  };

  return (
    <div className="space-y-6" data-testid="till-mapping-page">
      <div>
        <h1 className="text-3xl font-bold">Till Mapping</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Link the names on each store's Day End report to the fields in the system. Saved links are used first; anything not linked is matched by name automatically.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-wrap items-end gap-4">
          <div className="space-y-1 min-w-[220px]">
            <Label>Store</Label>
            <Select value={storeId} onValueChange={changeStore}>
              <SelectTrigger data-testid="till-mapping-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
              <SelectContent>
                {tillStores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="till-mapping-file-input"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button variant="outline" disabled={!storeId || !!busy} onClick={() => fileRef.current?.click()} data-testid="till-mapping-upload-btn">
            {busy === 'upload' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload Day End PDF
          </Button>
          <div className="flex items-end gap-2">
            <div className="space-y-1 min-w-[180px]">
              <Label>Copy links from</Label>
              <Select value={copyFrom} onValueChange={setCopyFrom} disabled={!storeId}>
                <SelectTrigger data-testid="till-mapping-copy-select"><SelectValue placeholder="Another store" /></SelectTrigger>
                <SelectContent>
                  {tillStores.filter(s => s.id !== storeId).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" disabled={!copyFrom || !storeId || !!busy} onClick={copy} data-testid="till-mapping-copy-btn">
              <Copy className="h-4 w-4 mr-2" />Copy
            </Button>
          </div>
          <Button className="ml-auto" disabled={!dirty || !!busy} onClick={save} data-testid="till-mapping-save-btn">
            {busy === 'save' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Mapping
          </Button>
        </CardContent>
      </Card>

      {data && mappings && (
        <>
          <p className="text-sm text-muted-foreground" data-testid="till-mapping-info">
            Till: <span className="font-medium uppercase">{data.store.till_system || 'not set'}</span>
            {data.last_report_date ? ` · Last report read: ${data.last_report_date}` : ' · No report read yet'}
            {dirty && <span className="text-amber-600 font-medium"> · Unsaved changes</span>}
          </p>
          <KeyTotals totals={data.totals} defaults={data.total_defaults} value={mappings.totals}
            onChange={(k, v) => { setMappings(m => ({ ...m, totals: { ...(m.totals || {}), [k]: v } })); setDirty(true); }} />
          <Tabs defaultValue="shop">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList>
                {SECTIONS.map(s => (
                  <TabsTrigger key={s.key} value={s.key} data-testid={`till-mapping-tab-${s.key}`}>
                    {s.label} ({data.items[s.key]?.length || 0})
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="flex items-center gap-2">
                <Switch id="only-unmatched" checked={onlyUnmatched} onCheckedChange={setOnlyUnmatched} data-testid="till-mapping-unmatched-toggle" />
                <Label htmlFor="only-unmatched" className="text-sm">Show only not matched</Label>
              </div>
            </div>
            {SECTIONS.map(s => (
              <TabsContent key={s.key} value={s.key} className="mt-4">
                <MappingSection category={s.key} items={data.items[s.key] || []} targets={data.targets[s.key] || []}
                  links={mappings[s.key] || {}} onChange={setLink} onlyUnmatched={onlyUnmatched}
                  qtyTargets={s.key === 'shop' ? data.targets.shop_qty || [] : undefined} qtyLinks={mappings.shop_qty || {}} />
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
}
